import { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowLeft, Loader2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { SEO } from "@/components/SEO";

const WORDPRESS_URL = "https://www.mostwanted.ro";
const BASENAME = "/emergent-landing";
const WP_PORTFOLIO_API = `${WORDPRESS_URL}/wp-json/mostwanted/v1/portfolio`;

// Animation
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
};

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial="hidden" animate={isInView ? "visible" : "hidden"} variants={staggerContainer} className={className}>
      {children}
    </motion.div>
  );
};

const PortfolioPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const fetchPortfolio = async () => {
      try {
        const response = await axios.get(WP_PORTFOLIO_API);
        const portfolioData = response.data.map((item, index) => ({
          ...item,
          // Assign aspect ratios for visual variety
          aspect: index % 5 === 0 ? 'tall' : index % 7 === 0 ? 'wide' : 'square'
        }));
        setProjects(portfolioData);
        
        // Extract unique categories
        const cats = [...new Set(portfolioData.map(p => p.category))];
        setCategories(cats);
      } catch (error) {
        console.error("Failed to fetch portfolio:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, []);

  const filteredProjects = filter === "all" 
    ? projects 
    : projects.filter(p => p.category === filter);

  const breadcrumbs = [
    { name: "Acasă", url: "https://www.mostwanted.ro/" },
    { name: "Portofoliu", url: "https://www.mostwanted.ro/portfolio/" }
  ];

  return (
    <div className="bg-white min-h-screen">
      <SEO 
        page="portfolio" 
        breadcrumbs={breadcrumbs}
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-center justify-between h-20 md:h-24">
            <Link to="/" className="font-['Cormorant_Garamond'] text-xl md:text-2xl tracking-wide text-[#1a1a1a]">
              MOST <span className="font-normal">WANTED</span> DESIGN
            </Link>
            <Link to="/" className="flex items-center gap-2 font-['Manrope'] text-sm uppercase tracking-widest text-[#1a1a1a] hover:text-[#737373] transition-colors">
              <ArrowLeft className="w-4 h-4" /> Inapoi
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 relative overflow-hidden">
        <div className="absolute -left-10 top-20 w-[120px] h-[500px] bg-[#1a1a1a] transform -rotate-[25deg] hidden md:block" />
        <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
          <AnimatedSection>
            <motion.p variants={fadeInUp} className="font-['Manrope'] text-sm uppercase tracking-[0.3em] text-[#737373] mb-4">
              Portofoliu
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-4xl md:text-6xl lg:text-7xl text-[#1a1a1a] leading-tight max-w-4xl mb-6">
              Proiectele noastre
            </motion.h1>
            <motion.p variants={fadeInUp} className="font-['Manrope'] text-lg text-[#737373] max-w-2xl">
              Exploreaza colectia noastra de proiecte de design interior - de la cafenele si restaurante la apartamente si spatii comerciale.
            </motion.p>
          </AnimatedSection>
        </div>
      </section>

      {/* Filter Section */}
      {categories.length > 1 && (
        <section className="py-8 border-b border-[#e5e5e5]">
          <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setFilter("all")}
                className={`font-['Manrope'] text-sm uppercase tracking-widest px-4 py-2 transition-colors ${
                  filter === "all" ? "bg-[#1a1a1a] text-white" : "text-[#737373] hover:text-[#1a1a1a]"
                }`}
              >
                Toate ({projects.length})
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`font-['Manrope'] text-sm uppercase tracking-widest px-4 py-2 transition-colors ${
                    filter === cat ? "bg-[#1a1a1a] text-white" : "text-[#737373] hover:text-[#1a1a1a]"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Portfolio Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-10 h-10 animate-spin text-[#737373]" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {filteredProjects.map((project, i) => (
                  <motion.a
                    key={project.id}
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: (i % 12) * 0.05 }}
                    className={`group relative overflow-hidden cursor-pointer bg-[#f5f5f5] ${
                      project.aspect === 'tall' ? 'row-span-2' : 
                      project.aspect === 'wide' ? 'col-span-2' : ''
                    }`}
                  >
                    {project.image ? (
                      <img
                        src={project.image}
                        alt={project.title}
                        className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                          project.aspect === 'tall' ? 'h-full min-h-[400px]' : 
                          project.aspect === 'wide' ? 'aspect-[2/1]' : 'aspect-square'
                        }`}
                      />
                    ) : (
                      <div className={`w-full bg-[#e5e5e5] flex items-center justify-center ${
                        project.aspect === 'tall' ? 'h-full min-h-[400px]' : 
                        project.aspect === 'wide' ? 'aspect-[2/1]' : 'aspect-square'
                      }`}>
                        <span className="font-['Cormorant_Garamond'] text-2xl text-[#737373]">MW</span>
                      </div>
                    )}
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                      <p className="font-['Manrope'] text-xs text-white/70 uppercase tracking-wider mb-2">{project.category}</p>
                      <h3 className="font-['Cormorant_Garamond'] text-lg md:text-xl text-white mb-2 line-clamp-2">{project.title}</h3>
                      <span className="inline-flex items-center gap-2 font-['Manrope'] text-xs text-white/70 uppercase tracking-wider">
                        Vezi proiect <ExternalLink className="w-3 h-3" />
                      </span>
                    </div>
                  </motion.a>
                ))}
              </div>

              {/* Results count */}
              <div className="mt-12 text-center">
                <p className="font-['Manrope'] text-sm text-[#737373]">
                  {filteredProjects.length} proiecte {filter !== "all" && `in categoria "${filter}"`}
                </p>
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#1a1a1a]">
        <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16 text-center">
          <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-5xl text-white mb-4">
            Ai un proiect in minte?
          </h2>
          <p className="font-['Manrope'] text-white/60 mb-8 max-w-xl mx-auto">
            Contacteaza-ne pentru o consultatie gratuita si hai sa discutam despre cum putem transforma spatiul tau.
          </p>
          <Link
            to="/#contact"
            className="inline-flex items-center gap-3 px-10 py-5 bg-white text-[#1a1a1a] font-['Manrope'] text-sm uppercase tracking-widest hover:bg-[#f5f5f5] transition-colors"
          >
            Contacteaza-ne
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#0a0a0a]">
        <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <Link to="/" className="font-['Cormorant_Garamond'] text-xl text-white">
              MOST <span className="font-normal">WANTED</span> DESIGN
            </Link>
            <div className="flex gap-8">
              <a href="https://www.instagram.com/mostwanted.design/" target="_blank" rel="noopener noreferrer" className="font-['Manrope'] text-sm text-white/60 hover:text-white transition-colors">Instagram</a>
              <a href="https://www.facebook.com/mostwanted.ro" target="_blank" rel="noopener noreferrer" className="font-['Manrope'] text-sm text-white/60 hover:text-white transition-colors">Facebook</a>
            </div>
            <p className="font-['Manrope'] text-sm text-white/60">© 2025 Most Wanted Design</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PortfolioPage;
